/* global Excel, console */

import type { TabularSheetCellValue, TabularSheetPayload } from '../tabular-sheet-payload.model';
import type { TabularSheetWriteMode } from '../tabular-sheet-write-mode.model';
import { buildUniqueTraceTableName } from './utils/build-unique-trace-table-name.util';

/**
 * Light table preset — header row + filters when structured as a table. Override via {@link TabularSheetPayload.excelTableStyle}.
 */
const DEFAULT_TABLE_STYLE = 'TableStyleLight1';

const EXCEL_MAX_COLUMN_INDEX = 16_383;

function assertPayloadGridShape(payload: TabularSheetPayload): void {
    const headerCount = payload.columnHeaders.length;
    if (headerCount === 0) {
        throw new Error('Tabular sheet payload must include at least one column header.');
    }
    for (let i = 0; i < payload.rows.length; i++) {
        if (payload.rows[i].length !== headerCount) {
            throw new Error(
                `Row ${i} has ${payload.rows[i].length} cell(s); expected ${headerCount} to match headers.`
            );
        }
    }
}

/**
 * Writes header + data from A1, removes prior Trace table(s) for this logical report on **this sheet only**,
 * clears from A1 over a rectangle large enough to remove leftover cells when a fetch is smaller than before,
 * then creates an Excel table with a workbook-unique name so another sheet’s output is not affected.
 *
 * Clear bounds are {@link Excel.Worksheet.getUsedRangeOrNullObject} merged with the new grid (row/column
 * maxima)—that is Excel’s notion of extent on this sheet after table removal. Unrelated content in that
 * rectangle may be cleared if it shares the same used area as Trace output.
 */
async function writeTabularSheetReplaceToWorksheet(
    payload: TabularSheetPayload
): Promise<{ sheetName: string; sheetId: string; tableCreated: boolean }> {
    assertPayloadGridShape(payload);
    const headerCount = payload.columnHeaders.length;

    return Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet();
        sheet.load(['name', 'id']);
        await context.sync();

        const sheetName = sheet.name;
        const uniqueTableName = buildUniqueTraceTableName(payload.tableName, sheet.id);

        const legacy = sheet.tables.getItemOrNullObject(payload.tableName);
        const current = sheet.tables.getItemOrNullObject(uniqueTableName);
        legacy.load('isNullObject');
        current.load('isNullObject');
        await context.sync();

        for (const t of [legacy, current]) {
            if (!t.isNullObject) {
                t.delete();
            }
        }
        await context.sync();

        const tableValues: TabularSheetCellValue[][] = [
            [...payload.columnHeaders],
            ...payload.rows.map((r) => [...r]),
        ];
        const newRowCount = tableValues.length;
        const newColCount = headerCount;

        const usedAfterDelete = sheet.getUsedRangeOrNullObject();
        usedAfterDelete.load(['isNullObject', 'rowCount', 'columnCount']);
        await context.sync();

        let wipeRows = newRowCount;
        let wipeCols = newColCount;
        if (!usedAfterDelete.isNullObject) {
            wipeRows = Math.max(usedAfterDelete.rowCount, newRowCount);
            wipeCols = Math.max(usedAfterDelete.columnCount, newColCount);
        }

        if (wipeRows > 0 && wipeCols > 0) {
            sheet.getRangeByIndexes(0, 0, wipeRows, wipeCols).clear();
            await context.sync();
        }

        const targetRange = sheet.getRangeByIndexes(0, 0, newRowCount, newColCount);
        targetRange.values = tableValues;
        targetRange.load('address');
        await context.sync();

        let tableCreated = false;
        const style = payload.excelTableStyle ?? DEFAULT_TABLE_STYLE;
        try {
            const table = sheet.tables.add(targetRange.address, true);
            table.name = uniqueTableName;
            table.style = style;
            table.getRange().format.autofitColumns();
            await context.sync();
            tableCreated = true;
        } catch (tableError) {
            console.warn('Table creation failed, data remains written as range.', tableError);
            targetRange.format.autofitColumns();
            await context.sync();
        }

        return { sheetName, sheetId: sheet.id, tableCreated };
    });
}

/**
 * Paste-values refresh: writes new rows to a scratch range aligned with the table data body, then
 * `copyFrom` values into `table.getDataBodyRange()`. Throws if the table is missing
 * or row/column counts differ so the caller can fall back to {@link writeTabularSheetReplaceToWorksheet}.
 */
async function writeTabularSheetRefreshToWorksheet(
    payload: TabularSheetPayload
): Promise<{ sheetName: string; sheetId: string; tableCreated: boolean }> {
    assertPayloadGridShape(payload);
    const headerCount = payload.columnHeaders.length;
    const dataRowCount = payload.rows.length;
    if (dataRowCount === 0) {
        throw new Error('TRACE_TABULAR_REFRESH_EMPTY_ROWS_USE_REPLACE');
    }

    return Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet();
        sheet.load(['name', 'id']);
        await context.sync();

        const uniqueName = buildUniqueTraceTableName(payload.tableName, sheet.id);
        const byUnique = sheet.tables.getItemOrNullObject(uniqueName);
        const byLegacy = sheet.tables.getItemOrNullObject(payload.tableName);
        byUnique.load('isNullObject');
        byLegacy.load('isNullObject');
        await context.sync();

        if (byUnique.isNullObject && byLegacy.isNullObject) {
            throw new Error('TRACE_TABULAR_REFRESH_NO_TABLE');
        }
        const table = byUnique.isNullObject ? byLegacy : byUnique;

        const body = table.getDataBodyRange();
        body.load(['rowCount', 'columnCount', 'rowIndex', 'columnIndex']);
        const used = sheet.getUsedRangeOrNullObject();
        used.load(['isNullObject', 'columnCount']);
        await context.sync();

        if (body.rowCount !== dataRowCount || body.columnCount !== headerCount) {
            throw new Error('TRACE_TABULAR_REFRESH_SHAPE_MISMATCH');
        }

        const usedCols = used.isNullObject ? 0 : used.columnCount;
        const margin = 4;
        let scratchCol0 = Math.max(usedCols + 2, 200);
        if (scratchCol0 + headerCount + margin > EXCEL_MAX_COLUMN_INDEX) {
            scratchCol0 = EXCEL_MAX_COLUMN_INDEX - headerCount - margin;
        }
        if (scratchCol0 < 0) {
            throw new Error('TRACE_TABULAR_REFRESH_NO_SCRATCH_COLUMN');
        }

        const scratchRange = sheet.getRangeByIndexes(
            body.rowIndex,
            scratchCol0,
            body.rowCount,
            body.columnCount
        );
        scratchRange.values = payload.rows.map((r) => [...r]);
        await context.sync();

        body.copyFrom(scratchRange, Excel.RangeCopyType.values, false, false);
        await context.sync();

        return { sheetName: sheet.name, sheetId: sheet.id, tableCreated: true };
    });
}

/**
 * Writes tabular report data to the active worksheet.
 *
 * **Refresh vs replace (product):**
 * - **replace** — write values from A1, then create a structured Excel table with a per-sheet unique name
 *   so headers and filters behave correctly without colliding with tables on other sheets.
 * - **refresh** — when `mode === 'refresh'`, attempts value-only paste into the existing table; on any
 *   failure (missing table, shape mismatch, Office error), logs and performs a **replace** instead.
 *
 * Entry points (`ReportingTaskPaneFilters.fetchReport`) set `mode` explicitly: **Fetch report** uses
 * **replace**; **Refresh data** uses **refresh** when the filter fingerprint matches the last successful fetch.
 */
export async function writeTabularSheetToWorksheet(
    payload: TabularSheetPayload,
    mode: TabularSheetWriteMode = 'replace'
): Promise<{ sheetName: string; sheetId: string; tableCreated: boolean }> {
    if (mode === 'refresh') {
        try {
            return await writeTabularSheetRefreshToWorksheet(payload);
        } catch (e) {
            console.warn('Tabular refresh failed; falling back to full replace.', e);
        }
    }
    return writeTabularSheetReplaceToWorksheet(payload);
}
